import { useEffect, useRef, useState } from 'react';
import { Icon } from '@gplibraries/react/components';
import { LoadingCircle } from '@gplibraries/react/components'
import './EditUserModal.scss';

export default function EditUserModal({
  user,
  allPermissions,
  onClose,
  onSave,
  onDelete,
  dealerInfo
}) {
   const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user.name);
  const [, setEmail] = useState(user.email);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const overlayRef = useRef(null);

  const divisions = Array.isArray(dealerInfo?.divisions) ? dealerInfo.divisions : [];
  const hasGreatPlains = divisions.includes("01");
  const hasLandPride = divisions.includes("02");

  const emptyPermissions = {
    GP: { enabled: false, codes: {} },
    LP: { enabled: false, codes: {} },
  };

  const [selectedCodes, setSelectedCodes] = useState(
    user.permissionsByDivision ?? emptyPermissions
  );


  /* Close on ESC */
      
  useEffect(() => {

    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', handleKey);

    setName(user.name);
    setEmail(user.email);
    setSelectedCodes(user.permissionsByDivision ?? emptyPermissions);

    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, user]);


    function toggleDivision(division) {
      setSelectedCodes((prev) => ({
        ...prev,
        [division]: {
          ...prev[division],
          enabled: !prev[division].enabled,
        },
      }));
    }

    function toggleCode(division, code) {
      setSelectedCodes((prev) => ({
        ...prev,
        [division]: {
          ...prev[division],
          codes: {
            ...prev[division].codes,
            [code]: !prev[division].codes?.[code],
          },
        },
      }));
    }

   
    async function handleSave() {
      setSaving(true);
      const lpValues = Object.entries(selectedCodes.LP.codes || {})
        .filter(([, enabled]) => enabled)
        .map(([code]) => String(code));

      const gpValues = Object.entries(selectedCodes.GP.codes || {})
        .filter(([, enabled]) => enabled)
        .map(([code]) => String(code));

      const permissions = [
        {
          division: "02",
          values: lpValues, 
        },
        {
          division: "01",
          values: gpValues, 
        },
      ];

      await onSave({
        username: user.username,
        permissions,
      });
      setSaving(false);
    }



  async function handleDelete() {
    await onDelete(user.employee_user);
    onClose();
  }

  return (
    <div
      className="edit-user-modal-backdrop"
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="edit-user-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="modal-header">
          <h2>Edit User</h2>
          <button onClick={onClose} className="icon-button">
            <Icon glyph="x-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Name */}
          <label className='name'>
            <span className='name__label'>Name: </span>
            <div className="name__value">
                  {name}
            </div>
          </label>

          {/* Permissions */}
          <div className="permissions-section">
            <h3 className='perm-title'>Permissions</h3>
            {saving ? (
              <div className="permissions-loading">
                <LoadingCircle/>
              </div>
            ) : (
              <>
                {hasGreatPlains && (
                  <div className="permission-group">
                    <label className="group-header">
                      <input
                        type="checkbox"
                        checked={selectedCodes.GP.enabled}
                        onChange={() => toggleDivision("GP")}
                      />
                      Great Plains
                    </label>

                    {selectedCodes.GP.enabled && (
                      <div className="permissions-list permissions-list--indented">
                        {Object.entries(allPermissions).map(([code, label]) => (
                          <label key={`gp-${code}`}>
                            <input
                              type="checkbox"
                              checked={!!selectedCodes.GP.codes?.[code]}
                              onChange={() => toggleCode("GP", code)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {hasLandPride && (
                  <div className="permission-group">
                    <label className="group-header">
                      <input
                        type="checkbox"
                        checked={selectedCodes.LP.enabled}
                        onChange={() => toggleDivision("LP")}
                      />
                      Land Pride
                    </label>

                    {selectedCodes.LP.enabled && (
                      <div className="permissions-list permissions-list--indented">
                        {Object.entries(allPermissions).map(([code, label]) => (
                          <label key={`lp-${code}`}>
                            <input
                              type="checkbox"
                              checked={!!selectedCodes.LP.codes?.[code]}
                              onChange={() => toggleCode("LP", code)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="danger-btn" onClick={() => setConfirmDelete(true)}>
            Delete User
          </button>

          <div className="footer-actions">
            <button onClick={onClose} className="secondary-btn">
              Cancel
            </button>
            <button onClick={handleSave} className="primary-btn">
              Save Changes
            </button>
          </div>
        </div>
      </div>
      {confirmDelete && (
        <div className="confirm-delete-backdrop">
            <div className="confirm-delete-modal">
            <h3>Delete User</h3>
            <p>
                Are you sure you want to delete <strong>{user.name}</strong>? 
                This action cannot be undone.
            </p>

            <div className="confirm-actions">
                <button
                className="secondary-btn"
                onClick={() => setConfirmDelete(false)}
                >
                Cancel
                </button>

                <button
                className="danger-btn"
                onClick={handleDelete}
                >
                Yes, Delete
                </button>
            </div>
            </div>
        </div>
        )}
    </div>
    
  );

}
